import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { FUELS, TRANSMISSIONS, DRIVETRAINS } from "@/lib/format";
import { TID } from "@/constants/tids";

const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export default function SearchBar() {
  const [brands, setBrands] = useState([]);
  const [suggestPool, setSuggestPool] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [openSug, setOpenSug] = useState(false);
  const [form, setForm] = useState({
    brand: "",
    q: "",
    year_min: "",
    price_min: "",
    price_max: "",
    fuel: "",
    transmission: "",
    drivetrain: "",
    km_max: "",
  });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/vehicles/brands").then((r) => setBrands(r.data)).catch(() => {});
    api
      .get("/vehicles/suggestions")
      .then((r) => {
        const { brands: b = [], combos = [], models = [] } = r.data || {};
        // Ordine: mărci, combinații „marcă model", modele — deduplicat.
        setSuggestPool([...new Set([...b, ...combos, ...models])]);
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildAndGo = (state) => {
    const params = new URLSearchParams();
    Object.entries(state).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    nav(`/masini?${params.toString()}`);
  };

  const submit = (e) => {
    e.preventDefault();
    setOpenSug(false);
    buildAndGo(form);
  };

  const onQueryChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, q: value }));
    const nq = normalize(value);
    if (!nq) {
      setSuggestions([]);
      setOpenSug(false);
      return;
    }
    const matched = suggestPool
      .filter((s) => {
        const ns = normalize(s);
        return ns.startsWith(nq) || ns.split(" ").some((w) => w.startsWith(nq));
      })
      .slice(0, 7);
    setSuggestions(matched);
    setOpenSug(matched.length > 0);
  };

  const pickSuggestion = (s) => {
    const next = { ...form, q: s };
    setForm(next);
    setOpenSug(false);
    buildAndGo(next);
  };

  const inp =
    "bg-[#0a0a0a] border border-white/[0.08] rounded-lg h-10 px-3.5 w-full text-white placeholder-white/40 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 text-[13px] outline-none transition-colors";

  return (
    <form
      onSubmit={submit}
      className="relative rounded-2xl border border-white/[0.08] bg-[#111111]/95 backdrop-blur-xl px-6 sm:px-8 py-6 sm:py-7 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)]"
    >
      <div className="absolute -top-3 left-6 sm:left-8 inline-flex items-center gap-2 bg-[#D4AF37] rounded-full px-4 py-1.5">
        <Search className="h-3.5 w-3.5 text-[#0a0a0a]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0a0a0a]">
          Caută rapid
        </span>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-end gap-5 lg:gap-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-3 flex-1">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 mb-1.5">
              Marcă
            </label>
            <select
              data-testid={TID.searchBrand}
              className={inp}
              value={form.brand}
              onChange={set("brand")}
              aria-label="Marcă"
            >
              <option value="">Toate mărcile</option>
              {brands.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
          <div className="relative">
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 mb-1.5">
              Caută
            </label>
            <input
              data-testid={TID.searchSmart}
              className={inp}
              placeholder="Ex: Ford, 4x4, diesel"
              value={form.q}
              onChange={onQueryChange}
              onFocus={() => setOpenSug(suggestions.length > 0)}
              onBlur={() => setTimeout(() => setOpenSug(false), 150)}
              autoComplete="off"
            />
            {openSug && suggestions.length > 0 && (
              <ul
                data-testid="search-suggestions"
                className="absolute z-40 mt-1.5 left-0 right-0 max-h-64 overflow-y-auto rounded-lg border border-white/[0.12] bg-[#0f0f0f] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.9)] py-1"
              >
                {suggestions.map((s) => (
                  <li key={s}>
                    <button
                      type="button"
                      data-testid="search-suggestion-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickSuggestion(s);
                      }}
                      className="w-full text-left px-3.5 py-2.5 text-[13px] text-white/80 hover:bg-white/[0.06] hover:text-[#D4AF37] transition-colors"
                    >
                      {s}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 mb-1.5">
              An minim
            </label>
            <input
              data-testid={TID.searchYear}
              className={inp}
              type="number"
              inputMode="numeric"
              placeholder="2015"
              value={form.year_min}
              onChange={set("year_min")}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 mb-1.5">
              Km maxim
            </label>
            <input
              data-testid={TID.searchKmMax}
              className={inp}
              type="number"
              inputMode="numeric"
              placeholder="150.000"
              value={form.km_max}
              onChange={set("km_max")}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 mb-1.5">
              Preț minim
            </label>
            <input
              data-testid={TID.searchPriceMin}
              className={inp}
              type="number"
              inputMode="numeric"
              placeholder="€"
              value={form.price_min}
              onChange={set("price_min")}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 mb-1.5">
              Preț maxim
            </label>
            <input
              data-testid={TID.searchPriceMax}
              className={inp}
              type="number"
              inputMode="numeric"
              placeholder="€"
              value={form.price_max}
              onChange={set("price_max")}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 mb-1.5">
              Combustibil
            </label>
            <select
              data-testid={TID.searchFuel}
              className={inp}
              value={form.fuel}
              onChange={set("fuel")}
              aria-label="Combustibil"
            >
              <option value="">Toate</option>
              {FUELS.map((f) => (
                <option key={f}>{f}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 mb-1.5">
              Cutie
            </label>
            <select
              data-testid={TID.searchTransmission}
              className={inp}
              value={form.transmission}
              onChange={set("transmission")}
              aria-label="Cutie de viteze"
            >
              <option value="">Toate</option>
              {TRANSMISSIONS.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 mb-1.5">
              Tracțiune
            </label>
            <select
              data-testid={TID.filterDrivetrain}
              className={inp}
              value={form.drivetrain}
              onChange={set("drivetrain")}
              aria-label="Tracțiune"
            >
              <option value="">Toate</option>
              {DRIVETRAINS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          data-testid={TID.searchSubmit}
          type="submit"
          className="gold-shine shrink-0 inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full h-10 px-6 font-bold text-[12px] uppercase tracking-[0.16em] transition-colors shadow-[0_8px_24px_-6px_rgba(212,175,55,0.5)]"
        >
          <Search className="h-3.5 w-3.5" />
          Caută
        </button>
      </div>
    </form>
  );
}
